// ============================================================================
// TRIAL BALANCE UPLOAD MODAL  (FSA-2.1.6)
// ============================================================================
// Multi-step wizard with named-TB support:
//   Step 0 — Mode (skipped when no sources exist).
//     • If exactly one named TB exists: inline confirm strip
//       ("Replace 'Main TB v1' with the new upload?" + small "or add new"
//       link).
//     • If multiple named TBs exist: explicit picker —
//       "Replace which?" radio list + "Add new TB for consolidation" button.
//   Step 1 — Select file + source + name field + period (when adding new)
//             or locked name + period (when revising).
//   Step 2 — Preview parsed rows / totals / diagnostics.
//   Step 3 — Impact view (only when revising; shows added / removed /
//             amount-changed ledgers vs the previous version).
//   Step 4 — Commit + success indicator.
//
// Upload returns { consolidated, impact } per FSA-2.1.6. The impact is
// rendered in Step 3 from the response.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import FileUploadZone from 'components/common/FileUploadZone';
import notificationService from 'services/notificationService';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import type { ID } from 'api/types';
import type {
  TrialBalanceSource,
  TrialBalancePreviewResponse,
  ParsedHeaderColumn,
  TrialBalanceColumnMap,
  RawPreviewRow,
  SourceTbRegistryItem,
  SourceReplacementImpact,
} from 'api/types/fsa/trialBalance.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  onClose: () => void;
  onUploaded: () => void;
  /**
   * FSA-2.3 — optional initial purpose. Set to 'OPENING' when launching
   * from the first-year CTA so the auditor doesn't have to flip the
   * selector themselves. Ignored when revising (purpose locks to the
   * existing source's purpose).
   */
  initialPurpose?: 'CURRENT' | 'OPENING';
}

type Mode = 'revise' | 'new';
type Step = 0 | 1 | 2 | 3 | 4;

const SOURCE_OPTIONS: { value: TrialBalanceSource; label: string }[] = [
  { value: 'EXCEL',     label: 'Excel (.xlsx)' },
  { value: 'CSV',       label: 'CSV' },
  { value: 'TALLY_XML', label: 'Tally XML export' },
];

const ACCEPT: Record<TrialBalanceSource, string> = {
  EXCEL:     '.xlsx',
  CSV:       '.csv,text/csv',
  TALLY_XML: '.xml,text/xml,application/xml',
  MANUAL:    '*',
};

// Default TB name by purpose — a Prior Year (OPENING) upload defaults to
// "Opening TB" so it's distinguishable from the current-year "Main TB" when
// multiple TBs are listed.
const defaultTbName = (purpose: 'CURRENT' | 'OPENING'): string =>
  purpose === 'OPENING' ? 'Opening TB' : 'Main TB';

// All-unmapped map — used to reset roles when the auditor picks a new header
// row (the backend then auto-detects roles on that row).
const EMPTY_COLUMN_MAP: TrialBalanceColumnMap = {
  ledgerIndex: -1, amountIndex: -1, debitIndex: -1, creditIndex: -1,
  groupIndex: -1, noteIndex: -1, classificationIndex: -1, openingIndex: -1, headerRowIndex: -1,
};

const TrialBalanceUploadModal: React.FC<Props> = ({ isOpen, engagementId, onClose, onUploaded, initialPurpose }) => {
  // -- Workflow state --
  const [step, setStep] = useState<Step>(0);
  const [mode, setMode] = useState<Mode>('new'); // 'revise' or 'new'
  const [liveSources, setLiveSources] = useState<SourceTbRegistryItem[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [replaceSourceId, setReplaceSourceId] = useState<ID | null>(null);

  // -- File / metadata --
  const [source, setSource] = useState<TrialBalanceSource>('EXCEL');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState(defaultTbName(initialPurpose ?? 'CURRENT'));
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  // FSA-2.3 Roll Forward — CURRENT (default working TB whose closings feed
  // CY columns) or OPENING (audited prior-year closing balances seeded
  // manually for first-year engagements with no prior in PRIMA but a known
  // opening position). When revising, this is locked to the existing
  // source's purpose (the backend rejects mismatches).
  const [purpose, setPurpose] = useState<'CURRENT' | 'OPENING'>(initialPurpose ?? 'CURRENT');

  // -- Network / response --
  const [previewing, setPreviewing] = useState(false);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<TrialBalancePreviewResponse | null>(null);
  const [impact, setImpact] = useState<SourceReplacementImpact | null>(null);
  // -- Column mapping (confirm/override before committing) --
  const [detectedColumns, setDetectedColumns] = useState<ParsedHeaderColumn[]>([]);
  const [columnMap, setColumnMap] = useState<TrialBalanceColumnMap | null>(null);
  const columnMapRef = useRef(columnMap);
  columnMapRef.current = columnMap;
  const [remapping, setRemapping] = useState(false);
  // Set true when the auditor closes mid-request so a late-resolving
  // preview/impact response can't pop the modal back to a stale step.
  const closedRef = useRef(false);
  // Post-commit "success screen" timer — cleared on unmount so a late fire
  // can't call onUploaded() after the engagement screen has gone away.
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clears every in-flight flag. Kept separate from the field reset because
  // BOTH the open effect and reset() need it: this modal is mounted for the
  // life of the engagement screen (the parent renders it unconditionally and
  // toggles `isOpen`), so any flag left latched survives close → reopen.
  // `committing` in particular gates every footer button AND handleClose, so
  // a latched TRUE traps the auditor in a modal with no working control —
  // escapable only by reloading the page.
  const resetTransientFlags = useCallback(() => {
    setPreviewing(false);
    setLoadingImpact(false);
    setRemapping(false);
    setCommitting(false);
  }, []);

  const reset = useCallback(() => {
    setStep(0);
    setMode('new');
    setReplaceSourceId(null);
    setSource('EXCEL');
    setFile(null);
    setName(defaultTbName(initialPurpose ?? 'CURRENT'));
    setPeriodStart('');
    setPeriodEnd('');
    setPurpose(initialPurpose ?? 'CURRENT');
    setPreview(null);
    setImpact(null);
    setDetectedColumns([]);
    setColumnMap(null);
    resetTransientFlags();
  }, [initialPurpose, resetTransientFlags]);

  useEffect(() => () => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
  }, []);

  // Fetch live source TBs when the modal opens. We DON'T auto-skip to
  // Step 1 here even when there are no sources for the current purpose,
  // because the auditor may want to flip the Purpose toggle and replace
  // an existing TB of the OTHER purpose. Step 0 always renders the
  // Purpose tiles + the filtered picker; the inline-confirm /
  // explicit-picker / no-existing branches are decided per-purpose
  // inside the render.
  useEffect(() => {
    if (!isOpen) return;
    closedRef.current = false;
    // Belt-and-braces: never open on top of a latched flag from the previous
    // session. reset() already clears them on the normal paths; this covers
    // any path that closes without one (and makes the invariant local to the
    // open, so a future early-return can't reintroduce the trap).
    resetTransientFlags();
    setLoadingSources(true);
    fsaTrialBalanceService.listSources(engagementId)
      .then(r => {
        const sources = (r.data ?? [])
          .filter(s => !s.isSuperseded && !s.isRemovedFromConsolidation);
        setLiveSources(sources);

        // Always land on Step 0 — even on a true first upload — so the
        // auditor chooses the Upload purpose (Current TB vs Prior Year TB)
        // up-front. Step 0 renders the per-purpose picker, including the
        // "no existing <purpose> — add the first one" branch when this
        // engagement has no sources yet. (Previously a first upload skipped
        // straight to Step 1, which locked the purpose to Current TB and
        // hid the Prior Year TB option entirely.)
        setStep(0);
      })
      .catch(err => {
        notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load source TBs.');
      })
      .finally(() => setLoadingSources(false));
  }, [isOpen, engagementId, resetTransientFlags]);

  const handleClose = useCallback(() => {
    // Only a commit (an in-flight DB write, and bounded) blocks close.
    // Parsing the preview and computing the impact are reads the auditor
    // must always be able to back out of — otherwise a slow or empty
    // response traps them with no way to dismiss the modal.
    if (committing) return;
    closedRef.current = true;
    reset(); // also clears previewing / loadingImpact / remapping
    onClose();
  }, [committing, onClose, reset]);

  // ── Step 0 transitions ────────────────────────────────────────────
  const handlePickRevise = useCallback((sourceId: ID, sourceName: string) => {
    setMode('revise');
    setReplaceSourceId(sourceId);
    setName(sourceName);
    // Lock purpose to whatever the existing source is — the backend
    // rejects mismatches. Look it up from the registry list we already
    // have in state.
    const existing = liveSources.find(s => s.id === sourceId);
    if (existing?.purpose === 'OPENING' || existing?.purpose === 'CURRENT') {
      setPurpose(existing.purpose);
    }
    // Seed the mapping from the prior version of this TB so a same-format
    // replacement maps without re-confirming. The first preview passes it.
    if (existing?.columnMapJson) {
      try { setColumnMap(JSON.parse(existing.columnMapJson) as TrialBalanceColumnMap); }
      catch { setColumnMap(null); }
    }
    setStep(1);
  }, [liveSources]);

  const handlePickNew = useCallback(() => {
    setMode('new');
    setReplaceSourceId(null);
    // First TB of this purpose → sensible default name ("Main TB" for
    // Current, "Opening TB" for Prior Year). Additional TBs added for
    // consolidation start blank so the auditor types a distinguishing name
    // (e.g. "Branch A", "HO").
    const samePurpose = liveSources.filter(s => s.purpose === purpose);
    setName(samePurpose.length === 0 ? defaultTbName(purpose) : '');
    setStep(1);
  }, [liveSources, purpose]);

  // ── Step 1 → Preview ──────────────────────────────────────────────
  const handleNextFromSelect = useCallback(async () => {
    if (!file) { notificationService.toast.warning('Pick a file first.'); return; }
    if (!name.trim()) { notificationService.toast.warning('TB name is required.'); return; }
    setPreviewing(true);
    try {
      // Auto-detect by default; on replace, a mapping seeded from the prior
      // version of this TB (columnMapRef) pre-applies so a same-format file
      // maps without re-confirming.
      const response = await fsaTrialBalanceService.preview(file, source, columnMapRef.current);
      if (closedRef.current) return; // auditor backed out while parsing
      setPreview(response.data);
      setDetectedColumns(response.data.columns ?? []);
      setColumnMap(response.data.effectiveMap ?? null);
      setStep(2);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to preview file.');
    } finally {
      setPreviewing(false);
    }
  }, [file, source, name]);

  // ── Re-parse with an auditor-edited column mapping (Step 2) ─────────
  const reapplyMapping = useCallback(async (nextMap: TrialBalanceColumnMap) => {
    if (!file) return;
    setColumnMap(nextMap);            // optimistic — dropdowns reflect the choice
    setRemapping(true);
    try {
      const response = await fsaTrialBalanceService.preview(file, source, nextMap);
      if (closedRef.current) return;
      setPreview(response.data);
      // A header-row change alters the columns, so refresh the detected set.
      setDetectedColumns(response.data.columns ?? []);
      if (response.data.effectiveMap) setColumnMap(response.data.effectiveMap);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to re-apply mapping.');
    } finally {
      setRemapping(false);
    }
  }, [file, source]);

  // Header-row pick — re-parse treating the chosen file row as the header.
  // Roles are reset (the new row has its own columns) so the backend
  // auto-detects them afresh on that row.
  const applyHeaderRow = useCallback((fileRow: number) => {
    void reapplyMapping({ ...EMPTY_COLUMN_MAP, headerRowIndex: fileRow });
  }, [reapplyMapping]);

  // ── Step 2 → Impact (revise) OR Commit (new) ───────────────────────
  // Revise: fetch the impact preview from the backend (no commit), then
  //         transition to Step 3 so the user can review before committing.
  // New:    no impact to show — skip to commit directly.
  const handleNextFromPreview = useCallback(async () => {
    if (!file) return;
    if (mode === 'new') {
      // Fall through to commit directly — no Impact step for new sources.
      void handleCommit();
      return;
    }
    if (!replaceSourceId) return;
    setLoadingImpact(true);
    try {
      const response = await fsaTrialBalanceService.previewImpact(
        engagementId, file, source, name.trim(), replaceSourceId, columnMap,
      );
      if (closedRef.current) return; // auditor backed out while comparing
      setImpact(response.data);
      setStep(3);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to compute impact.');
    } finally {
      setLoadingImpact(false);
    }
    // handleCommit is referenced for the 'new' branch — declared below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, mode, replaceSourceId, engagementId, source, name, columnMap]);

  // ── Step 3 (revise) or Step 2 (new) → Commit → Success ─────────────
  const handleCommit = useCallback(async () => {
    if (!file) return;
    setCommitting(true);
    try {
      await fsaTrialBalanceService.upload(
        engagementId, file, source, name.trim(), replaceSourceId,
        periodStart || null, periodEnd || null, purpose, columnMap,
      );
      // The write is done — drop `committing` NOW, before the success screen.
      // Leaving it latched used to poison the next upload in the same session:
      // this modal stays mounted, so the stale TRUE disabled every footer
      // button (Next: View Impact / Commit) and made handleClose a no-op, and
      // only a page reload got the auditor out. Hit reliably on a replacement,
      // since replacing implies an earlier successful upload.
      setCommitting(false);
      setStep(4);
      commitTimerRef.current = setTimeout(() => {
        commitTimerRef.current = null;
        reset();
        onUploaded();
      }, 800);
    } catch (err: any) {
      setCommitting(false);
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Upload failed.');
    }
  }, [file, source, engagementId, name, replaceSourceId, periodStart, periodEnd, purpose, columnMap, reset, onUploaded]);

  if (!isOpen) return null;

  const hasErrors = !!preview?.diagnostics.some(d => d.severity === 'ERROR');
  // Filter the Step 0 picker by the chosen Purpose so the auditor only
  // sees same-purpose sources as replace candidates. CURRENT TB uploads
  // never list Prior Year TBs (and vice versa) — the backend rejects
  // mismatched-purpose replaces anyway; here we just hide the
  // unreachable options up-front.
  const sourcesForPurpose = liveSources.filter(s => s.purpose === purpose);
  const singleSource = sourcesForPurpose.length === 1 ? sourcesForPurpose[0] : null;
  const purposeLabel = purpose === 'OPENING' ? 'Prior Year TB' : 'Current TB';

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Upload Trial Balance</h2>
          <button className="modal-close" onClick={handleClose} disabled={committing}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Step strip — Step 0 (Mode) hidden when there's no pre-existing TB. */}
        {step >= 1 && (
          <div style={stepStripStyle}>
            <StepDot n={1} label="Select"  active={step === 1} done={step > 1} />
            <StepDot n={2} label="Preview" active={step === 2} done={step > 2} />
            {mode === 'revise' && (
              <StepDot n={3} label="Impact" active={step === 3} done={step > 3} />
            )}
            <StepDot n={mode === 'revise' ? 4 : 3} label="Commit" active={step === 4} done={false} />
          </div>
        )}

        <div className="modal-content" style={{ flex: 1, overflowY: 'auto' }}>
          {step === 0 && (
            <div>
              {loadingSources ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading source TBs…</div>
              ) : (
                <div>
                  {/* Purpose tiles — chosen BEFORE the source picker so the
                      picker below shows same-purpose sources only. CURRENT
                      and Prior Year TBs maintain independent version chains
                      and Replace/Consolidate decisions. */}
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">
                      Upload purpose <span className="text-danger">*</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setPurpose('CURRENT')}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: 6,
                          border: '1px solid',
                          borderColor: purpose === 'CURRENT' ? '#2563eb' : 'var(--form-border-color)',
                          background: purpose === 'CURRENT' ? '#eff6ff' : 'var(--bg-primary)',
                          color: purpose === 'CURRENT' ? '#1e40af' : 'var(--text-primary)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        title="Current-year TB — closings feed CY columns on BS / PL / Notes."
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>📊 Current TB</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                          Current FY — feeds CY columns
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPurpose('OPENING')}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: 6,
                          border: '1px solid',
                          borderColor: purpose === 'OPENING' ? '#d97706' : 'var(--form-border-color)',
                          background: purpose === 'OPENING' ? '#fef3c7' : 'var(--bg-primary)',
                          color: purpose === 'OPENING' ? '#92400e' : 'var(--text-primary)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        title="Prior year's full pre-closing TB (all ledgers including P&L). Feeds PY columns for comparatives."
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>📅 Prior Year TB</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                          Prior FY's pre-closing TB — feeds PY columns
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Source picker — scoped to the chosen purpose. */}
                  {singleSource ? (
                    <div style={inlineConfirmStyle}>
                      <div style={{ fontSize: '0.95rem', marginBottom: 8 }}>
                        An existing {purposeLabel} <strong>{singleSource.name} v{singleSource.version}</strong> is on file.
                        Replace it with this upload?
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => handlePickRevise(singleSource.id, singleSource.name)}
                        >
                          Yes, replace
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handlePickNew}
                        >
                          Or add as new {purposeLabel} for consolidation
                        </button>
                      </div>
                    </div>
                  ) : sourcesForPurpose.length > 1 ? (
                    <div>
                      <div style={{ fontSize: '0.95rem', marginBottom: 12 }}>
                        Multiple {purposeLabel}s exist for this engagement. Replace one, or add a new one for consolidation.
                      </div>
                      <div style={modePickerListStyle}>
                        {sourcesForPurpose.map(s => (
                          <button
                            key={String(s.id)}
                            type="button"
                            onClick={() => handlePickRevise(s.id, s.name)}
                            style={modePickerItemStyle}
                          >
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.name} <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>v{s.version}</span></div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                {s.source} · {s.entryCount} ledgers · uploaded by {s.uploadedByName ?? '—'}
                              </div>
                            </div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Replace →</span>
                          </button>
                        ))}
                      </div>
                      <div style={{ marginTop: 14, textAlign: 'center' }}>
                        <button className="btn btn-secondary" onClick={handlePickNew}>
                          <IconSystem.add size={14} /> Add new {purposeLabel} for consolidation
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={inlineConfirmStyle}>
                      <div style={{ fontSize: '0.9rem', marginBottom: 10, color: 'var(--text-secondary)' }}>
                        No existing {purposeLabel} on this engagement. Click Continue to add the first one.
                      </div>
                      <button className="btn btn-primary" onClick={handlePickNew}>
                        Continue →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">
                  TB name <span className="text-danger">*</span>
                  {mode === 'revise' && (
                    <span style={{ marginLeft: 6, color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 400 }}>
                      (locked — replacing existing TB)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  readOnly={mode === 'revise'}
                  placeholder={mode === 'new' ? 'e.g. Branch A, HO, Old System Apr–Aug' : undefined}
                />
              </div>
              {/* Purpose was chosen in Step 0 — show as a read-only badge here. */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Upload purpose</label>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: purpose === 'OPENING' ? '#fef3c7' : '#dbeafe',
                  color: purpose === 'OPENING' ? '#92400e' : '#1e40af',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}>
                  {purpose === 'OPENING' ? '📅 Prior Year TB' : '📊 Current TB'}
                </div>
                {purpose === 'CURRENT' && liveSources.some(s => s.purpose === 'OPENING') && (
                  <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    ℹ️ A Prior Year TB is already configured on this engagement. The Opening Balance column in this file will be ignored — PY comparatives come from the Prior Year TB.
                  </div>
                )}
              </div>
              <SyutSelect
                label="Source format"
                value={source}
                onChange={(v) => { setSource((v as TrialBalanceSource) ?? 'EXCEL'); setFile(null); }}
                options={SOURCE_OPTIONS}
                isClearable={false}
              />
              <div style={{ marginTop: 12 }}>
                <FileUploadZone
                  accept={ACCEPT[source]}
                  value={file}
                  onChange={setFile}
                  hint={`Drop a ${SOURCE_OPTIONS.find(s => s.value === source)?.label} file here, or click to browse`}
                  maxSizeBytes={50 * 1024 * 1024}
                />
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Not sure of the format?{' '}
                  <button
                    type="button"
                    onClick={() => fsaTrialBalanceService.downloadUploadTemplate()
                      .catch(() => notificationService.toast.error('Could not download the template.'))}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-primary, #2563eb)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
                  >
                    Download the upload template
                  </button>{' '}
                  (Ledger · Group · Note · Classification · Amount).
                </div>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Period Start (optional)</label>
                  <input type="date" className="form-input" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Period End (optional)</label>
                  <input type="date" className="form-input" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && preview && (
            <div>
              <div style={summaryStripStyle}>
                <SummaryStat label="Rows" value={preview.rowCount.toLocaleString('en-IN')} />
                <SummaryStat label="Debit total" value={preview.debitTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })} />
                <SummaryStat label="Credit total" value={preview.creditTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })} />
                <SummaryStat
                  label="Difference"
                  value={preview.difference.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  warning={Math.abs(preview.difference) > 0.005}
                />
              </div>

              {preview.diagnostics.length > 0 && (
                <div style={diagBlockStyle}>
                  {preview.diagnostics.map((d, i) => (
                    <div key={i} style={{ color: d.severity === 'ERROR' ? '#dc2626' : '#92400e', fontSize: '0.8rem' }}>
                      <strong>{d.severity}</strong>{d.rowNumber != null ? ` (row ${d.rowNumber})` : ''}: {d.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm / correct the auto-detected column mapping before
                  committing. Editing a dropdown re-parses with the override. */}
              {/* Header-row picker — always available (incl. when detection
                  failed) so the auditor can point at the real header row. */}
              {(preview.rawRows?.length ?? 0) > 0 && (
                <HeaderRowPicker
                  rawRows={preview.rawRows}
                  headerRow={columnMap?.headerRowIndex && columnMap.headerRowIndex >= 1
                    ? columnMap.headerRowIndex
                    : preview.headerRowIndex}
                  busy={remapping || previewing}
                  onChange={applyHeaderRow}
                />
              )}
              {detectedColumns.length > 0 && columnMap && (
                <ColumnMapPanel
                  columns={detectedColumns}
                  map={columnMap}
                  busy={remapping || previewing}
                  onChange={reapplyMapping}
                />
              )}

              {/* Show Note / Classification columns only when the file
                  carried them — confirms the pre-mapping parsed correctly. */}
              {(() => {
              const hasImported = preview.rows.some(r => r.importedNote || r.importedClassification);
              return (
              <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                <table style={previewTableStyle}>
                  <thead style={{ position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Ledger</th>
                      <th style={thStyle}>Group</th>
                      {hasImported && <th style={thStyle}>Note</th>}
                      {hasImported && <th style={thStyle}>Classification</th>}
                      {/* One Amount per TB — the upload purpose (Current / Prior
                          Year) decides whether it feeds CY or PY, so a separate
                          Opening column here only confused (it read as 0 on a
                          Prior Year upload). */}
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 200).map(r => (
                      <tr key={r.rowNumber}>
                        <td style={tdStyle}>{r.rowNumber}</td>
                        <td style={tdStyle}>{r.ledgerName}</td>
                        <td style={tdStyle}>{r.parentGroup ?? ''}</td>
                        {hasImported && <td style={tdStyle}>{r.importedNote ?? ''}</td>}
                        {hasImported && <td style={tdStyle}>{r.importedClassification ?? ''}</td>}
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{r.closingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.rows.length > 200 && (
                  <div style={{ padding: 8, textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Showing first 200 of {preview.rows.length} rows. Full set commits on next step.
                  </div>
                )}
              </div>
              );
              })()}
            </div>
          )}

          {step === 3 && impact && (
            <div>
              <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
                Impact of replacing <strong>{impact.tbName} v{impact.oldVersion}</strong> with <strong>v{impact.newVersion}</strong>:
              </div>
              <div style={summaryStripStyle}>
                <SummaryStat label="Added"    value={String(impact.addedLedgers.length)} />
                <SummaryStat label="Removed"  value={String(impact.removedLedgers.length)} warning={impact.removedWithClassifications.length > 0} />
                <SummaryStat label="Amount changed" value={String(impact.amountChangedLedgers.length)} />
                <SummaryStat label="Re-grouped" value={String(impact.groupChangedLedgers.length)} />
                <SummaryStat label="Class. may need re-confirm" value={String(impact.removedWithClassifications.length)} warning={impact.removedWithClassifications.length > 0} />
              </div>

              {/* Identical re-upload — make it explicit the diff ran and found
                  nothing, so an empty list never reads as "stuck". */}
              {!impact.hasChanges && (
                <div style={{ ...diagBlockStyle, background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    No differences detected — the new version is identical to the current one (same ledgers, balances, and groups). You can still commit it (a new version is recorded) or cancel.
                  </div>
                </div>
              )}

              <ImpactSection title="Added ledgers" rows={impact.addedLedgers.map(l => ({ name: l.ledgerName, sub: l.parentGroup ?? '', amount: l.closingAmount }))} />
              <ImpactSection title="Removed ledgers" rows={impact.removedLedgers.map(l => ({ name: l.ledgerName, sub: l.parentGroup ?? '', amount: l.closingAmount }))} flag />
              <ImpactSection
                title="Amount changed"
                rows={impact.amountChangedLedgers.map(l => ({ name: l.ledgerName, sub: l.parentGroup ?? '', amount: l.newClosingAmount, oldAmount: l.oldClosingAmount }))}
              />
              <ImpactSection
                title="Re-grouped (balance unchanged)"
                rows={impact.groupChangedLedgers.map(l => ({
                  name: l.ledgerName,
                  sub: `${l.oldParentGroup || '—'} → ${l.newParentGroup || '—'}`,
                  amount: l.closingAmount,
                }))}
              />

              {impact.removedWithClassifications.length > 0 && (
                <div style={{ ...diagBlockStyle, background: '#fef3c7', borderColor: '#f59e0b' }}>
                  <strong style={{ fontSize: '0.85rem', color: '#92400e' }}>Classifications may need re-confirmation</strong>
                  <div style={{ fontSize: '0.8rem', marginTop: 4, color: '#92400e' }}>
                    {impact.removedWithClassifications.length} removed ledger{impact.removedWithClassifications.length === 1 ? '' : 's'} had a classification on the consolidated TB. Unless another live source still contains them, those classifications are gone.
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <IconSystem.check size={36} />
              <div style={{ fontWeight: 600, marginTop: 8 }}>Trial balance committed</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Consolidated TB rebuilt — review the classification queue.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {step === 0 && (
            <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          )}
          {step === 1 && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep(0)}>
                Back
              </button>
              <button className="btn btn-primary" onClick={handleNextFromSelect} disabled={!file || previewing || !name.trim()}>
                {previewing ? 'Parsing…' : 'Next: Preview'}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setStep(1)}
                disabled={committing || loadingImpact}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleNextFromPreview}
                disabled={committing || loadingImpact || hasErrors}
                title={hasErrors ? 'Resolve errors before continuing' : undefined}
              >
                {mode === 'revise'
                  ? (loadingImpact ? 'Computing impact…' : 'Next: View Impact')
                  : (committing ? 'Committing…' : 'Commit Upload')}
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setStep(2)}
                disabled={committing}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCommit}
                disabled={committing}
              >
                {committing ? 'Committing…' : 'Commit Upload'}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// -----------------------------------------------------------------------
// Header-row picker (Step 2). Lets the auditor point at the real header row
// when the file has banner/title rows above it, or when auto-detect found
// none. Picking a row re-parses and auto-detects the column roles on it.
const HeaderRowPicker: React.FC<{
  rawRows: RawPreviewRow[];
  headerRow: number;
  busy: boolean;
  onChange: (fileRow: number) => void;
}> = ({ rawRows, headerRow, busy, onChange }) => {
  const summary = (cells: (string | null)[]) =>
    cells.map(c => (c ?? '').trim()).filter(Boolean).slice(0, 6).join(' · ') || '(blank row)';
  return (
    <div style={mapPanelStyle}>
      <div style={mapPanelHeaderStyle}>
        <span><IconSystem.list size={13} /> <strong>Header row</strong> — the row with the column titles</span>
      </div>
      <select
        className="form-input"
        style={{ fontSize: '0.8rem', padding: '0.3rem 0.45rem', width: '100%' }}
        value={String(headerRow)}
        disabled={busy}
        onChange={e => onChange(Number(e.target.value))}
      >
        {rawRows.map(r => (
          <option key={r.fileRow} value={String(r.fileRow)}>
            {`Row ${r.fileRow}: ${summary(r.cells)}`}
          </option>
        ))}
      </select>
    </div>
  );
};

// -----------------------------------------------------------------------
// Column mapping confirm/override panel (Step 2). Auto-detect pre-fills it;
// the auditor confirms or corrects each role before committing. Native
// selects keep the dense 6-field grid light. "Amount" is one signed column;
// a Debit + Credit pair is the alternative (Net = Debit − Credit).
const ColumnMapPanel: React.FC<{
  columns: ParsedHeaderColumn[];
  map: TrialBalanceColumnMap;
  busy: boolean;
  onChange: (next: TrialBalanceColumnMap) => void;
}> = ({ columns, map, busy, onChange }) => {
  // Single-Amount vs Debit/Credit-pair is an explicit UI mode, NOT derived
  // purely from whether the columns are mapped — otherwise ticking the box
  // before picking the columns (which are -1 until chosen) would instantly
  // revert to Amount. Promote to Dr/Cr when those columns get set (e.g.
  // auto-detected), but never auto-demote, so the auditor's choice sticks.
  const [drcrMode, setDrcrMode] = useState<boolean>(map.debitIndex >= 0 || map.creditIndex >= 0);
  useEffect(() => {
    if (map.debitIndex >= 0 || map.creditIndex >= 0) setDrcrMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.debitIndex, map.creditIndex]);
  // Amount (single) and the Debit/Credit pair are mutually exclusive — keep
  // exactly one model set so a stale Amount column can't suppress the Dr/Cr
  // netting on the backend (which would zero-out credit-balance ledgers).
  const set = (patch: Partial<TrialBalanceColumnMap>) => {
    let next = { ...map, ...patch };
    if (next.debitIndex >= 0 || next.creditIndex >= 0) next = { ...next, amountIndex: -1 };
    else if (next.amountIndex >= 0) next = { ...next, debitIndex: -1, creditIndex: -1 };
    onChange(next);
  };

  const Picker: React.FC<{
    label: string; value: number; required?: boolean;
    onPick: (idx: number) => void;
  }> = ({ label, value, required, onPick }) => (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label className="form-label" style={{ fontSize: '0.75rem', marginBottom: 2 }}>
        {label}{required && <span className="text-danger"> *</span>}
      </label>
      <select
        className="form-input"
        style={{ fontSize: '0.8rem', padding: '0.3rem 0.45rem' }}
        value={String(value)}
        disabled={busy}
        onChange={e => onPick(Number(e.target.value))}
      >
        {!required && <option value="-1">— None —</option>}
        {required && value < 0 && <option value="-1">— Select —</option>}
        {columns.map(c => (
          <option key={c.index} value={String(c.index)}>{c.name}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div style={mapPanelStyle}>
      <div style={mapPanelHeaderStyle}>
        <span><IconSystem.edit size={13} /> <strong>Column mapping</strong> — confirm or correct, then continue</span>
        {busy && <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Re-reading…</span>}
      </div>
      <div className="form-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem 0.75rem' }}>
        <Picker label="Ledger" value={map.ledgerIndex} required onPick={idx => set({ ledgerIndex: idx })} />
        <Picker label="Ledger Group" value={map.groupIndex} onPick={idx => set({ groupIndex: idx })} />
        {!drcrMode ? (
          <Picker label="Amount" value={map.amountIndex} required onPick={idx => set({ amountIndex: idx })} />
        ) : (
          <>
            <Picker label="Debit" value={map.debitIndex} required onPick={idx => set({ debitIndex: idx })} />
            <Picker label="Credit" value={map.creditIndex} required onPick={idx => set({ creditIndex: idx })} />
          </>
        )}
        <Picker label="Note" value={map.noteIndex} onPick={idx => set({ noteIndex: idx })} />
        <Picker label="Classification" value={map.classificationIndex} onPick={idx => set({ classificationIndex: idx })} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        <input
          type="checkbox"
          checked={drcrMode}
          disabled={busy}
          onChange={e => {
            const on = e.target.checked;
            setDrcrMode(on);
            // Clear the opposite model so only one is sent + parsed.
            set(on ? { amountIndex: -1 } : { debitIndex: -1, creditIndex: -1 });
          }}
        />
        Source has separate Debit &amp; Credit columns (Net = Debit − Credit)
      </label>
    </div>
  );
};

// -----------------------------------------------------------------------
const StepDot: React.FC<{ n: number; label: string; active: boolean; done: boolean }> = ({ n, label, active, done }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      width: 24, height: 24, borderRadius: 999, fontSize: '0.75rem', fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: done ? '#16a34a' : active ? '#2563eb' : 'var(--bg-secondary)',
      color: (done || active) ? '#ffffff' : 'var(--text-secondary)',
      border: `1px solid ${done ? '#16a34a' : active ? '#2563eb' : 'var(--form-border-color)'}`,
    }}>{done ? '✓' : n}</span>
    <span style={{ fontSize: '0.8rem', fontWeight: active ? 600 : 500, color: active ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
      {label}
    </span>
  </div>
);

const SummaryStat: React.FC<{ label: string; value: string; warning?: boolean }> = ({ label, value, warning }) => (
  <div>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 700, color: warning ? '#dc2626' : 'var(--text-primary)' }}>{value}</div>
  </div>
);

const ImpactSection: React.FC<{
  title: string;
  rows: Array<{ name: string; sub: string; amount: number; oldAmount?: number }>;
  flag?: boolean;
}> = ({ title, rows, flag }) => {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {title} ({rows.length})
      </div>
      <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
        <table style={previewTableStyle}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={flag ? { background: '#fef3c7' } : undefined}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  {r.sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{r.sub}</div>}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {r.oldAmount != null && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textDecoration: 'line-through', marginRight: 6 }}>
                      {r.oldAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  )}
                  {r.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------
const mapPanelStyle: React.CSSProperties = {
  marginBottom: 12,
  padding: '0.6rem 0.85rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
};
const mapPanelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.8rem',
  marginBottom: 8,
};

const stepStripStyle: React.CSSProperties = {
  display: 'flex',
  gap: 18,
  padding: '0.65rem 1.25rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const summaryStripStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 12,
  marginBottom: 12,
  padding: '0.65rem 0.85rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
};
const diagBlockStyle: React.CSSProperties = {
  margin: '8px 0 12px',
  padding: 10,
  border: '1px solid #fde68a',
  background: '#fffbeb',
  borderRadius: 6,
};
const previewTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8rem',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};
const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-color)',
};
const inlineConfirmStyle: React.CSSProperties = {
  padding: '1.25rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
};
const modePickerListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const modePickerItemStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.65rem 0.85rem',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
};

export default TrialBalanceUploadModal;
